import * as THREE from 'three';
import { Pass, FullScreenQuad } from 'three/addons/postprocessing/Pass.js';

// ── CockpitOverlayPass ──────────────────────────────────────────────────────
//
// Re-draws the transparent cockpit interior (canopy glass + HUD) on top of the
// already-composited world so the volumetric cloud deck sits *behind* the glass.
//
// The problem it solves: clouds are a screen-space composer pass that clips its
// raymarch against the scene depth buffer. Transparent materials don't write
// depth (the HUD is even depthWrite/​depthTest-off), so at those pixels the depth
// buffer holds the far sky and the cloud pass paints clouds straight over the
// canopy and HUD, washing them out.
//
// The fix keeps the cockpit's transparent meshes out of the main RenderPass (App
// parks them on a dedicated layer while the cockpit camera is active) and redraws
// them here, after the cloud pass. Because this runs *before* bloom, the redrawn
// cockpit still flows through the rest of the pipeline (bloom/grain/NVG) exactly
// as it did when it lived in the beauty pass — so the HUD keeps its native glow.
//
// It copies the incoming buffer through a trivial full-screen blit, then clears
// only the depth and renders the scene restricted to the overlay layer, so the
// cockpit always draws on top of the clouds regardless of the (stale) depth.
//
// Clearing the depth also throws away what used to occlude the glass, though —
// without it the canopy paints straight over the cockpit interior, the panel and
// the canopy frame. So before redrawing the overlay meshes the pass re-lays the
// aircraft's depth (a colour-masked render of the occluder layer App tags the
// aircraft's opaque meshes with). Nothing else in the scene can sit between the
// cockpit camera and the glass, so the aircraft alone is enough.

const COPY_VERT = /* glsl */`
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const COPY_FRAG = /* glsl */`
  uniform sampler2D tDiffuse;
  varying vec2 vUv;
  void main() { gl_FragColor = texture2D(tDiffuse, vUv); }
`;

export default class CockpitOverlayPass extends Pass {
  /**
   * @param {{ scene: THREE.Scene, camera: THREE.PerspectiveCamera, layer: number,
   *           occluderLayer: number }} opts
   */
  constructor({ scene, camera, layer, occluderLayer }) {
    super();
    this.scene = scene;
    this.camera = camera;
    this.layer = layer;
    this.occluderLayer = occluderLayer;
    this.needsSwap = true;

    // Off until App turns it on for the cockpit camera; the pass is a no-op cost
    // (skipped by the composer) in every other view.
    this.enabled = false;

    this._copyMat = new THREE.ShaderMaterial({
      name: 'CockpitOverlayCopy',
      uniforms: { tDiffuse: { value: null } },
      vertexShader: COPY_VERT,
      fragmentShader: COPY_FRAG,
      depthTest: false,
      depthWrite: false,
    });
    this._fsq = new FullScreenQuad(this._copyMat);

    // Depth-only stand-in for the occluder pre-pass: writes depth, no colour.
    // DoubleSide so single-sided interior shells still lay down a near face.
    this._depthMat = new THREE.MeshBasicMaterial({
      name: 'CockpitOverlayDepth',
      colorWrite: false,
      side: THREE.DoubleSide,
    });
  }

  render(renderer, writeBuffer, readBuffer) {
    const target = this.renderToScreen ? null : writeBuffer;
    const prevAutoClear = renderer.autoClear;

    // 1) Blit the incoming (world + clouds) image into the target. autoClear
    //    clears the target's colour + depth first, so we start from a clean slab.
    this._copyMat.uniforms.tDiffuse.value = readBuffer.texture;
    renderer.setRenderTarget(target);
    renderer.autoClear = true;
    this._fsq.render(renderer);

    // 2) Redraw the cockpit on a fresh depth range so it sits over the clouds.
    //    clearDepth resets the (blitted) depth to far, so the cockpit lands on
    //    top of the composited image no matter what the stale depth held.
    //    The scene background (HDR sky) must be suppressed — the renderer always
    //    repaints it over the whole target, which would wipe the blitted world.
    //    Shadow maps were already computed by the main RenderPass this frame and
    //    nothing here casts shadows, so freeze them to avoid a redundant re-render.
    renderer.autoClear = false;
    const prevShadowAuto = renderer.shadowMap.autoUpdate;
    renderer.shadowMap.autoUpdate = false;
    const prevBackground = this.scene.background;
    this.scene.background = null;
    renderer.setRenderTarget(target);
    renderer.clearDepth();
    const camMask = this.camera.layers.mask;

    // 2a) Re-lay the aircraft's depth into that cleared buffer (colour masked),
    //     so the glass is occluded by the cockpit interior/frame the way it was
    //     when it still rendered inside the beauty pass.
    const prevOverride = this.scene.overrideMaterial;
    this.scene.overrideMaterial = this._depthMat;
    this.camera.layers.set(this.occluderLayer);
    renderer.render(this.scene, this.camera);
    this.scene.overrideMaterial = prevOverride;
    // The colour mask is per-material state, so it only comes back on when
    // something else draws. Restore it here so a frame where the overlay layer
    // renders nothing can't leave later passes unable to write colour.
    renderer.state.buffers.color.setMask(true);

    // 2b) Now the transparent cockpit itself, depth-tested against 2a.
    this.camera.layers.set(this.layer);
    renderer.render(this.scene, this.camera);

    this.camera.layers.mask = camMask;
    this.scene.background = prevBackground;
    renderer.shadowMap.autoUpdate = prevShadowAuto;

    renderer.autoClear = prevAutoClear;
  }

  dispose() {
    this._copyMat.dispose();
    this._depthMat.dispose();
    this._fsq.dispose();
  }
}
